import { SettingsPermissions } from "@redotech/account-common/permissions";
import { t } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { Navigate } from "react-router-dom";

const InternationalIndexRedirect = () => {
  const canManageOutboundCustoms = useHasPermission(
    SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
  );
  const canManageReturnCustoms = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
    SettingsPermissions.SET_SHIPPING_SETTINGS,
  );

  // The returns page needs returns or shipping permission; markets only needs
  // fulfillment.
  if (!canManageReturnCustoms && canManageOutboundCustoms) {
    return <Navigate replace to="markets" />;
  }

  return <Navigate replace to="returns" />;
};

export const internationalSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      get breadcrumb() {
        return t("International", {
          context: "merchant settings breadcrumb for international commerce",
        });
      },
    },
    path: "international",
    children: [
      { index: true, element: <InternationalIndexRedirect /> },
      {
        handle: {
          get breadcrumb() {
            return t("International Returns", {
              context: "merchant settings label for international returns",
            });
          },
          permission: [
            SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            SettingsPermissions.SET_SHIPPING_SETTINGS,
          ],
        },
        path: "returns",
        lazy: async () => {
          const { InternationalPage } =
            await import("../../../../setting/international/international-page");
          return { Component: InternationalPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Customs catalog", {
              context: "merchant settings label for the customs catalog",
            });
          },
          permission: [
            SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            SettingsPermissions.SET_SHIPPING_SETTINGS,
          ],
        },
        path: "catalog",
        lazy: async () => {
          const { CustomsCatalogPage } =
            await import("../../../../setting/international/catalog/customs-catalog-page");
          return { Component: CustomsCatalogPage };
        },
      },
      {
        handle: {
          get breadcrumb() {
            return t("Markets", {
              context: "merchant settings label for international markets",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "markets",
        children: [
          {
            index: true,
            lazy: async () => {
              const { MarketsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/markets/markets-page");
              return { Component: MarketsPage };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("Edit market");
              },
            },
            path: ":marketId",
            lazy: async () => {
              const { MarketEditorPage } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/markets/market-editor-page");
              return { Component: MarketEditorPage };
            },
          },
          {
            handle: {
              get breadcrumb() {
                return t("New market");
              },
            },
            path: "new",
            lazy: async () => {
              const { MarketEditorPage } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/markets/market-editor-page");
              return { Component: MarketEditorPage };
            },
          },
        ],
      },
      {
        handle: {
          get breadcrumb() {
            return t("Restrictions", {
              context:
                "merchant settings label for international shipping restrictions",
            });
          },
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "restrictions",
        lazy: async () => {
          const { RestrictionsSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/restrictions/restrictions-page");
          return { Component: RestrictionsSettingsPage };
        },
      },
    ],
  },
];
